import React, { useEffect, useState } from "react";
import { MdAddIcCall } from "react-icons/md";
import { CiLocationOn } from "react-icons/ci";
import { LiaCitySolid } from "react-icons/lia";
import { FaCity, FaIdCard, FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line, RiExchangeFundsLine } from "react-icons/ri";
import axios from "axios";
import franchiseImg from "../../../Assets/Dashboard/franchise.png";
import FranchiseStatusModal from "../../../components/Modals/FranchiseStatusModal";
import DeleteClientModal from "../../../components/Modals/DeleteClientModal";
import AddFranchiseModal from "../../../components/Modals/AddFranchiseModal";
import { FaShopLock } from "react-icons/fa6";
import { IoEyeOutline } from "react-icons/io5";
import FranchiseDetailsModal from "../../Modals/FranchiseDetailsModal";

const FranchiseList = ({ status, query, page, itemsPerPage, onDataChange }) => {
  const [loading, setLoading] = useState(false);
  const [franchise, setFranchise] = useState([]);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchFranchise();
  }, []);

  const fetchFranchise = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/franchise/get-franchises`
      );
      setFranchise(res.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = franchise.filter(
    (item) =>
      item.status?.toLowerCase() === status.toLowerCase() &&
      (item.franchiseName?.toLowerCase().includes(query.toLowerCase()) ||
        item.email?.toLowerCase().includes(query.toLowerCase()))
  );

  const startIndex = (page - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  useEffect(() => {
    onDataChange(totalPages, filtered.length);
  }, [filtered.length, totalPages]);

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/franchise/delete-franchise/${selectedFranchise._id}`
      );
      setShowDeleteModal(false);
      fetchFranchise();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    fetchFranchise(); // Refresh data after status change
  };

  const handleViewDetails = (franchise) => {
    setSelectedFranchise(franchise);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedFranchise(null);
  };

  return (
    <section className="px-2 mb-4">
      {loading ? (
        <div className="text-center py-6 text-gray-600">Loading...</div>
      ) : paginated.length === 0 ? (
        <div className="flex justify-center">
          <p className="p-4 text-gray-500">
            No {status.toLowerCase()} franchises found.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {paginated.map((client, index) => (
            <div
              key={index}
              className="relative flex justify-between items-center border-2 p-4 rounded-md bg-white shadow-lg"
            >
              <div>
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() => handleViewDetails(client)}
                    className="text-gray-600 hover:text-gray-800 transition-colors"
                    aria-label="View franchise details"
                  >
                    <IoEyeOutline className="h-5 w-5" />
                  </button>

                  <button
                    onClick={() => {
                      setSelectedFranchise(client);
                      setShowAddModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    aria-label="Edit franchise"
                  >
                    <FaRegEdit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedFranchise(client);
                      setShowDeleteModal(true);
                    }}
                    className="text-red-600 hover:text-red-800 transition-colors"
                    aria-label="Delete franchise"
                  >
                    <RiDeleteBin6Line className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedFranchise(client);
                      setShowStatusModal(true);
                    }}
                    className="text-primaryColor hover:opacity-80 transition-opacity"
                    aria-label="Change franchise status"
                  >
                    <RiExchangeFundsLine className="h-5 w-5" />
                  </button>
                </div>
                <h1 className="font-medium text-xl text-gray-700">
                  {client.fullName || "Unnamed"}
                </h1>
                <p className="text-gray-500 text-xs">{client.email}</p>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <FaShopLock size={18} />
                    <p className="text-gray-500">{client.franchiseName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <MdAddIcCall size={18} />
                    <p className="text-gray-500">{client.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaIdCard size={18} />
                    <p className="text-gray-500">
                      {client.aadharCard || "1234 5678 9012"}
                    </p>
                  </div>
                </div>
              </div>
              <img
                src={client?.companyRegistrationProof || franchiseImg}
                alt="Franchise"
                className="w-20 mt-4"
              />
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showStatusModal && (
        <FranchiseStatusModal
          franchise={selectedFranchise}
          onClose={handleStatusModalClose}
        />
      )}
      {showDeleteModal && (
        <DeleteClientModal
          itemName={selectedFranchise?.franchiseName}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      {showAddModal && (
        <AddFranchiseModal
          data={selectedFranchise}
          onClose={() => setShowAddModal(false)}
        />
      )}
      {showDetailsModal && (
        <FranchiseDetailsModal
          franchise={selectedFranchise}
          onClose={handleCloseDetailsModal}
        />
      )}
    </section>
  );
};

export default FranchiseList;
